// src/components/RecipeLibrary.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../shared/components/layout/Layout';
import { Card, Button, Input, Alert, RecipeCard } from '../../../shared/components/ui';
import RecipeOptionsButton from '../components/RecipeOptionsButton';
import EditRecipeModal from '../components/EditRecipeModal';
import GenerateRecipeModal from '../components/GenerateRecipeModal';
import { recipeService } from '../services/recipeService';
import { useRecipeSave } from '../hooks/useRecipeSave';
import { useTranslation } from 'react-i18next';
import { mapRecipesToCardData } from '../utils/recipeMapper';

const RecipeLibrary = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { saveRecipe } = useRecipeSave();
    const [meals, setMeals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        is_favorite: '',
        is_archived: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [generatedRecipe, setGeneratedRecipe] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);

    // Cargar meals al montar el componente
    useEffect(() => {
        loadMeals();
    }, [currentPage, searchTerm, filters]);

    const loadMeals = async () => {
        setIsLoading(true);
        setError('');

        try {
            const result = await recipeService.getAllRecipes(
                currentPage,
                pagination.limit,
                searchTerm,
                filters
            );

            if (result.success) {
                setMeals(result.data.recipes);
                setPagination(result.data.pagination);
            } else {
                setError(t('recipe.library.loadError') || t('common.error'));
            }
        } catch (err) {
            setError(err.message || t('recipe.library.loadError') || t('common.error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        loadMeals();
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setCurrentPage(1);
    };

    const handleToggleFavorite = async (recipeId) => {
        try {
            await recipeService.toggleRecipeFavorite(recipeId);
            // Recargar la lista para actualizar el estado
            loadMeals();
        } catch (err) {
            setError(err.message || t('recipe.library.favoriteError') || t('common.error'));
        }
    };

    const handleArchive = async (recipeId) => {
        try {
            await recipeService.archiveRecipe(recipeId);
            loadMeals();
        } catch (err) {
            setError(err.message || t('recipe.library.archiveError') || t('common.error'));
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleViewDetails = (mealId) => {
        navigate(`/recipe/${mealId}`);
    };

    const handleEditRecipe = async (meal) => {
        try {
            const response = await recipeService.getRecipeById(meal.id);
            if (response.success) {
                setSelectedRecipe(response.recipe);
                setIsEditModalOpen(true);
            }
        } catch (err) {
            setError(err.message || t('recipe.library.loadError') || t('common.error'));
        }
    };

    const handleSaveRecipe = async (data) => {
        const recipeId = isCreateMode ? null : selectedRecipe?.id;

        await saveRecipe(data, recipeId, async () => {
            setIsEditModalOpen(false);
            setSelectedRecipe(null);
            setGeneratedRecipe(null);
            setIsCreateMode(false);
            // Recargar la lista de recetas
            await loadMeals();
        });
    };

    const handleRecipeGenerated = (recipe) => {
        // Cuando se genera una receta, guardarla y abrir el EditRecipeModal en modo creación
        setGeneratedRecipe(recipe);
        setIsCreateMode(true);
        setIsGenerateModalOpen(false);
        setIsEditModalOpen(true);
    };

    const handleOpenGenerateModal = () => {
        setIsGenerateModalOpen(true);
    };

    return (
        <Layout>
            <div className="max-w-6xl mx-auto p-6">
                <div className="mb-8">
                    <div className="">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                📚 {t('recipe.library.title')}
                            </h1>
                            <p className="text-gray-600 ">
                                {t('recipe.library.description') || ''}
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            onClick={handleOpenGenerateModal}
                            className="whitespace-nowrap mt-4"
                        >
                            {t('menu.edit.titleNew')}
                        </Button>
                    </div>
                </div>

                {/* Barra de búsqueda y filtros */}
                <Card className="mb-6">
                    <form onSubmit={handleSearch} className="space-y-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <Input
                                    type="text"
                                    placeholder={t('recipe.library.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full"
                                />
                            </div>
                            <Button type="submit" variant="primary">
                                {t('recipe.library.search')}
                            </Button>
                        </div>

                        {/* <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('recipe.library.filters.favorite.all')}
                                </label>
                                <select
                                    value={filters.is_favorite}
                                    onChange={(e) => handleFilterChange('is_favorite', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="">{t('recipe.library.filters.favorite.all')}</option>
                                    <option value="true">{t('recipe.library.filters.favorite.onlyFavorites')}</option>
                                    <option value="false">{t('recipe.library.filters.favorite.notFavorites')}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('recipe.library.filters.status.all')}
                                </label>
                                <select
                                    value={filters.is_archived}
                                    onChange={(e) => handleFilterChange('is_archived', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="">{t('recipe.library.filters.status.all')}</option>
                                    <option value="false">{t('recipe.library.filters.status.active')}</option>
                                    <option value="true">{t('recipe.library.filters.status.archived')}</option>
                                </select>
                            </div>
                        </div> */}
                    </form>
                </Card>

                {/* Mensaje de error */}
                {error && (
                    <Alert variant="error" className="mb-6">
                        {error}
                    </Alert>
                )}

                {/* Lista de meals */}
                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="text-lg text-gray-600">{t('recipe.library.loading')}</div>
                    </div>
                ) : meals.length === 0 ? (
                    <Card className="text-center py-12">
                        <div className="text-gray-500">
                            <div className="text-6xl mb-4">📚</div>
                            <h3 className="text-xl font-medium mb-2">{t('recipe.library.empty')}</h3>
                            <p>{t('recipe.library.noResults') || ''}</p>
                        </div>
                    </Card>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                            {mapRecipesToCardData(meals).map((cardData) => {
                                const originalRecipe = cardData.originalRecipe;
                                return (
                                    <RecipeCard
                                        key={cardData.id}
                                        image={cardData.image}
                                        title={cardData.title}
                                        description={cardData.description}
                                        cookTime={cardData.cookTime}
                                        difficulty={cardData.difficulty}
                                        rating={cardData.rating}
                                        tags={cardData.tags}
                                        onClick={() => handleViewDetails(cardData.id)}
                                        actionsComponent={
                                            <RecipeOptionsButton
                                                recipe={originalRecipe}
                                                onToggleFavorite={handleToggleFavorite}
                                                onEditRecipe={handleEditRecipe}
                                                onViewDetails={handleViewDetails}
                                                onArchive={handleArchive}
                                            />
                                        }
                                    />
                                );
                            })}
                        </div>

                        {/* Paginación */}
                        {pagination.total_pages > 1 && (
                            <Card className="flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                    {t('recipe.library.showing')} {((currentPage - 1) * pagination.limit) + 1} {t('recipe.library.of')} {Math.min(currentPage * pagination.limit, pagination.total)} {t('recipe.library.recipes')}
                                </div>

                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={!pagination.has_prev}
                                    >
                                        ← {t('common.previous')}
                                    </Button>

                                    <span className="px-3 py-1 text-sm text-gray-600">
                                        {t('menu.selector.page')} {currentPage} {t('common.of')} {pagination.total_pages}
                                    </span>

                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={!pagination.has_next}
                                    >
                                        {t('common.next')} →
                                    </Button>
                                </div>
                            </Card>
                        )}
                    </>
                )}

                {/* Modal de generación de receta con IA */}
                <GenerateRecipeModal
                    isOpen={isGenerateModalOpen}
                    onClose={() => setIsGenerateModalOpen(false)}
                    onRecipeGenerated={handleRecipeGenerated}
                />

                {/* Modal de edición/creación de receta */}
                <EditRecipeModal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setSelectedRecipe(null);
                        setGeneratedRecipe(null);
                        setIsCreateMode(false);
                    }}
                    recipe={selectedRecipe}
                    initialRecipe={generatedRecipe}
                    isCreateMode={isCreateMode}
                    onSave={handleSaveRecipe}
                />
            </div>
        </Layout>
    );
};

export default RecipeLibrary;
